<script lang="ts">
  import { addRoute } from '@integro/core';
  import { gettext, pgettext, ngettext, npgettext } from '@integro/gettext';
  addRoute({
    name: 'settings.vatPeriods',
    path: 'vat-periods',
    component: 'VATPeriods',
  });
</script>

<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'VATPeriods',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    modal: {
      type: Boolean,
      required: false,
      default: false,
    },
    modalName: {
      type: String,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const table = ref(null);

  const $refs = {
    get table() {
      return table.value;
    },
  };

  const items = ref(null);
  const frequencyOptions = ref(null);
  const ux = ref([]);
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    size: 'sm',
  }));
  const b_3343864469950044_columns = computed(() => [
    {
      name: 'name',
      header: $fn.value.pgettext('VATPeriod', 'Name'),
      width: '160px',
      required: true,
    },
    {
      name: 'frequency',
      header: $fn.value.pgettext('VATPeriod', 'Frequency'),
      type: 'select',
      options: frequencyOptions.value,
      valueField: 'value',
      textField: 'name',
      width: '140px',
      required: true,
    },
    {
      name: 'start',
      header: $fn.value.pgettext('VATPeriod', 'Start'),
      width: '160px',
      type: 'date',
      required: true,
    },
    {
      name: 'end',
      header: $fn.value.pgettext('VATPeriod', 'End'),
      width: '160px',
      type: 'date',
      required: true,
    },
    {
      name: 'due_date',
      header: $fn.value.pgettext('VATPeriod', 'Due date'),
      width: '160px',
      type: 'date',
      required: true,
    },
    {
      name: 'closed',
      header: $fn.value.pgettext('VATPeriod', 'Closed'),
      type: 'checkbox',
    },
  ]);

  const $data = computed(() => ({
    items: items.value,
    frequencyOptions: frequencyOptions.value,
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
    b_3343864469950044_columns: b_3343864469950044_columns.value,
  }));

  const $$load_items = async () => {
    return $fn.value.fetch('list_vat_periods');
  };
  const $$load_frequencyOptions = async () => {
    return $app.value.repo.get('ui_metadata', {
      path: 'vat_frequency_options',
    });
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'items' },
      { name: 'frequencyOptions' },
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
      {
        name: 'b_3343864469950044_columns',
        nodeId: '3343864469950044',
        dependsOn: ['frequencyOptions'],
      },
    ],
    loaders: { items: $$load_items, frequencyOptions: $$load_frequencyOptions },
    ux: {},
    dataRefs: {
      items,
      frequencyOptions,
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
      b_3343864469950044_columns,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const pageDocument = computed(() => {
    return null;
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    () => props.modal,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modal');
    }
  );
  watch(
    () => props.modalName,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modalName');
    }
  );

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_z87ozr1r3_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
      },
    };
  };
  const act_3343864469950044_script_jutbttzzf_value_callable = async (
    $event
  ) => {
    return $event.data.waitFor(
      $fn.value.crud('vat_period').upsert({ ...$event.data.row })
    );
  };
  const act_3343864469950044_script_jutbttzzf_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_3343864469950044_script_jutbttzzf_value_callable($event);
      },
    };
  };
  const act_3343864469950044_script_h79n6i1ia_value_callable = async (
    $event
  ) => {
    return $event.data.waitFor(
      $fn.value.crud('vat_period').delete({ ...$event.data.row })
    );
  };
  const act_3343864469950044_script_h79n6i1ia_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_3343864469950044_script_h79n6i1ia_value_callable($event);
      },
    };
  };

  const act_7315092382398562_closeModal_z87ozr1r3 =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_z87ozr1r3_getActionArgs,
    });
  const inst_evh_7315092382398562_close_sc8a27rtu =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_z87ozr1r3,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_sc8a27rtu],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_f29idhmj7 =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_81nflk8mj =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_f29idhmj7,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_81nflk8mj],
    event: 'reload',
  });
  const act_8320016629450276_reloadComponentData_qgkj1jx7s =
    new actionTypes.ReloadComponentDataAction({
      displayName: 'reload',
    });
  const inst_evh_8320016629450276_clickPreventStop_xpocevfrd =
    new actionTypes.EventHandlerImpl({
      action: act_8320016629450276_reloadComponentData_qgkj1jx7s,
      event: 'click.prevent.stop',
      displayName: 'reload',
    });
  const inst_evh_8320016629450276_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_8320016629450276_clickPreventStop_xpocevfrd],
      event: 'click.prevent.stop',
    });
  const act_3343864469950044_script_jutbttzzf = new actionTypes.ScriptAction({
    actionArgs: act_3343864469950044_script_jutbttzzf_getActionArgs,
  });
  const inst_evh_3343864469950044_save_vls129zgu =
    new actionTypes.EventHandlerImpl({
      action: act_3343864469950044_script_jutbttzzf,
      event: 'save',
      displayName: 'script',
    });
  const inst_evh_3343864469950044_save = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_3343864469950044_save_vls129zgu],
    event: 'save',
  });
  const act_3343864469950044_script_h79n6i1ia = new actionTypes.ScriptAction({
    actionArgs: act_3343864469950044_script_h79n6i1ia_getActionArgs,
  });
  const inst_evh_3343864469950044_delete_2jz8y4wkb =
    new actionTypes.EventHandlerImpl({
      action: act_3343864469950044_script_h79n6i1ia,
      event: 'delete',
      displayName: 'script',
    });
  const inst_evh_3343864469950044_delete = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_3343864469950044_delete_2jz8y4wkb],
    event: 'delete',
  });

  const actions = {
    inst_evh_7315092382398562_close_sc8a27rtu,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_81nflk8mj,
    inst_evh_7315092382398562_reload,
    inst_evh_8320016629450276_clickPreventStop_xpocevfrd,
    reload: inst_evh_8320016629450276_clickPreventStop_xpocevfrd,
    inst_evh_8320016629450276_clickPreventStop,
    inst_evh_3343864469950044_save_vls129zgu,
    inst_evh_3343864469950044_save,
    inst_evh_3343864469950044_delete_2jz8y4wkb,
    inst_evh_3343864469950044_delete,
  };

  const evh_7315092382398562_close_sc8a27rtu = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_sc8a27rtu.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_81nflk8mj = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_81nflk8mj.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8320016629450276_clickPreventStop_xpocevfrd = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_8320016629450276_clickPreventStop_xpocevfrd.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8320016629450276_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_8320016629450276_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3343864469950044_save_vls129zgu = async (event, scope) => {
    return await actions.inst_evh_3343864469950044_save_vls129zgu.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3343864469950044_save = async (event, scope) => {
    return await actions.inst_evh_3343864469950044_save.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3343864469950044_delete_2jz8y4wkb = async (event, scope) => {
    return await actions.inst_evh_3343864469950044_delete_2jz8y4wkb.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3343864469950044_delete = async (event, scope) => {
    return await actions.inst_evh_3343864469950044_delete.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="VATPeriods"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <div class="overflow-hidden d-flex flex-column h-100 p-3">
      <ItpBox alignItems="center" :gap="2" class="mb-3">
        <strong>
          {{ pgettext('Settings', 'VAT periods') }}
        </strong>
        <ItpButton
          icon="fa-sync-alt"
          :text="gettext('Reload')"
          variant="light"
          size="sm"
          :spinning="actions.reload.isRunning"
          @click.prevent.stop="
            evh_8320016629450276_clickPreventStop($event, {})
          "
        >
        </ItpButton>
      </ItpBox>
      <ItpDataTable
        tableId="Settings.VATPeriods"
        editable
        :isRowEditable="(p) => !p.locked"
        :value="items"
        :columns="b_3343864469950044_columns"
        class="itp-datatable--flex-fill"
        ref="table"
        v-if="loaded"
        @save="evh_3343864469950044_save($event, {})"
        @delete="evh_3343864469950044_delete($event, {})"
      >
      </ItpDataTable>
    </div>
  </ItpPage>
</template>
